import { createListenerMiddleware, isAnyOf, type ThunkDispatch, type UnknownAction } from "@reduxjs/toolkit";
import { authActions, logoutCmtsSession, refreshCmtsToken } from "./authSlice.js";
import { complaintActions } from "./complaintSlice.js";
import { adminActions } from "./adminSlice.js";
import { dashboardActions, fetchCmtsDashboardSummary } from "./dashboardSlice.js";
import { masterDataActions, fetchCmtsMasterDataPage } from "./masterDataSlice.js";
import { permissionActions } from "./permissionSlice.js";
import { pwaActions } from "./pwaSlice.js";
import { fetchCmtsReportCatalog, reportActions } from "./reportSlice.js";
import { tenantActions } from "./tenantSlice.js";
import { uiActions } from "./uiSlice.js";
import type { CmtsRootReducerState } from "./rootReducer.js";
import type { CmtsReduxThunkExtra } from "./thunkExtra.js";

type CmtsListenerDispatch = ThunkDispatch<CmtsRootReducerState, CmtsReduxThunkExtra, UnknownAction>;

export const listenerMiddleware = createListenerMiddleware<CmtsRootReducerState, CmtsListenerDispatch, CmtsReduxThunkExtra>();
export const cmtsListenerMiddleware = listenerMiddleware;

listenerMiddleware.startListening({
  actionCreator: authActions.loginBootstrapSucceeded,
  effect: async (_action, api) => {
    const session = api.getState().auth.session;
    if (!session?.tenantId) return;
    if (api.extra.repositories?.reports) {
      api.dispatch(fetchCmtsReportCatalog({ tenantId: session.tenantId }));
    }
    if (api.extra.repositories?.dashboards) {
      api.dispatch(fetchCmtsDashboardSummary({ dashboardType: "management", query: { tenantId: session.tenantId } }));
    }
    if (api.extra.repositories?.masterData) {
      api.dispatch(fetchCmtsMasterDataPage({ entityType: "priority", tenantId: session.tenantId }));
    }
  },
});

listenerMiddleware.startListening({
  matcher: isAnyOf(authActions.logoutSucceeded, logoutCmtsSession.fulfilled, refreshCmtsToken.rejected),
  effect: async (_action, api) => {
    api.dispatch(permissionActions.permissionsCleared());
    api.dispatch(tenantActions.tenantCleared());
    api.dispatch(masterDataActions.masterDataCleared());
    api.dispatch(complaintActions.complaintCleared());
    api.dispatch(adminActions.adminCleared());
    api.dispatch(dashboardActions.dashboardCleared());
    api.dispatch(reportActions.reportCleared());
    api.dispatch(uiActions.uiCleared());
  },
});

listenerMiddleware.startListening({
  actionCreator: authActions.tokenRefreshFailed,
  effect: async (_action, api) => {
    api.dispatch(authActions.logoutSucceeded());
  },
});

listenerMiddleware.startListening({
  actionCreator: uiActions.apiErrorRecorded,
  effect: async (action, api) => {
    if (action.payload.status === 403) {
      api.dispatch(permissionActions.permissionBlocked({
        message: action.payload.message,
        status: action.payload.status,
        correlationId: action.payload.correlationId,
      }));
    }
  },
});

listenerMiddleware.startListening({
  actionCreator: pwaActions.updateAvailable,
  effect: async (_action, api) => {
    api.dispatch(uiActions.toastShown({ tone: "info", message: "A CMTS application update is available." }));
  },
});
